import { z } from 'zod'
import { createNumericField } from './schemaUtils'

export const productPhysicalPropertiesSchema = z
  .object({
    id: z.number().optional(),
    weightMeasureUnitId: z.number().optional().nullable(),
    volumeMeasureUnitId: z.number().optional().nullable(),
    lengthMeasureUnitId: z.number().optional().nullable(),
    densityMeasureUnitId: z.number().optional().nullable(),
    isRectangular: z.boolean().optional(),
    useDimensions: z.boolean().optional(),
    netWeight: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .refine((val) => val !== undefined && val !== null, {
          message: 'Net Weight is required',
        })
        .optional()
    ),
    grossWeight: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .refine((val) => val !== undefined && val !== null, {
          message: 'Gross Weight is required',
        })
        .optional()
    ),
    volume: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    height: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    length: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    width: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    density: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    maxLoadingHeight: createNumericField(
      z
        .number()
        .positive()
        .max(99999999.9999, { message: 'Max 99,999,999.9999' })
        .nullable()
        .optional()
    ),
    updatedByUserId: z.number().optional(),
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
  })
  .optional()
  .superRefine((data, ctx) => {
    const providedPP = {
      useDimensions: false,
    }

    if (JSON.stringify(data) === JSON.stringify(providedPP)) {
      return
    }

    if (!data?.netWeight) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Net Weight is required',
        path: ['netWeight'],
      })
    }
    if (!data?.grossWeight) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Gross Weight is required',
        path: ['grossWeight'],
      })
    }
    if (data && data.density && (!data.densityMeasureUnitId || data.densityMeasureUnitId === -1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Density units are required',
        path: ['density'],
      })
    }
    if (data && data.netWeight && (!data.weightMeasureUnitId || data.weightMeasureUnitId === -1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Weight unit is required',
        path: ['netWeight'],
      })
    }
    if (data && data.volume && (!data.volumeMeasureUnitId || data.volumeMeasureUnitId === -1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Volume unit is required',
        path: ['volume'],
      })
    }
    if (
      data &&
      (data.length || data.width || data.height) &&
      (!data.lengthMeasureUnitId || data.lengthMeasureUnitId === -1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Length unit is required',
        path: ['length'],
      })
    }
    if (data) {
      if (data.isRectangular) {
        if (!data.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Length is required',
            path: ['length'],
          })
        }
        if (!data.width) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Width is required',
            path: ['width'],
          })
        }
        if (!data.height) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Height is required',
            path: ['height'],
          })
        }
      } else if (data.useDimensions) {
        if (!data.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Diameter is required',
            path: ['length'],
          })
        }
        if (!data.height) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Height is required',
            path: ['height'],
          })
        }
      }
      if (data.useDimensions === false) {
        if (!data.volume) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Volume is required',
            path: ['volume'],
          })
        }
        if (!data.density) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Density is required',
            path: ['density'],
          })
        }
      }
    }
  })
